/**
 * Snapshot in, events out. Pure — no clock of its own beyond the `now` you
 * hand it, no I/O, no database. Everything the app ever tells the user
 * originates here, so the bias throughout is towards saying nothing rather
 * than saying something wrong.
 */

import { hasPassed, isRefinement, parseDate, sameDate } from './dates.js';
import {
  NON_PRIMARY_SLOTS,
  type CollectionSnapshot,
  type DateSlot,
  type DiffEvent,
  type SlateDate,
  type Snapshot,
  type TitleSnapshot,
  type WorkRef,
} from './types.js';

export interface DiffOptions {
  /** Snapshot format version each side was written by. */
  prevVersion?: number;
  nextVersion?: number;
  /** For deciding whether a newly-listed work is news or a catalogue backfill. */
  now?: Date;
}

/** A newly-listed work older than this is backfill, not an announcement. */
const BACKFILL_AGE_DAYS = 90;

/**
 * Purely a bound on payload size, not a judgement. Relevance filtering does
 * the real work; if this many genuinely-forthcoming works appear at once,
 * something is wrong upstream and the batch is worth truncating rather than
 * writing a thousand rows.
 */
const SANITY_CAP = 50;

/**
 * Losing this proportion of a collection's works in one poll is a bad fetch,
 * not an author being un-published. See `isSuspectSnapshot`.
 */
const SUSPECT_LOSS_RATIO = 0.5;

export function diffSnapshots(
  prev: Snapshot | null,
  next: Snapshot,
  opts: DiffOptions = {},
): DiffEvent[] {
  const now = opts.now ?? new Date();

  // First sight of something. There is no "before", so nothing has changed.
  // Without this, adding a watchable would immediately report every date it
  // has ever had as brand new.
  if (prev === null) return [];

  // A snapshot format change means the two sides are not comparable. Resync
  // silently; the alternative is every user waking up to a wall of false
  // changes on deploy day.
  if (opts.prevVersion !== undefined && opts.nextVersion !== undefined) {
    if (opts.prevVersion !== opts.nextVersion) return [];
  }

  // Kind changes are a data error upstream, not news.
  if (prev.kind !== next.kind) return [];

  return prev.kind === 'title'
    ? diffTitle(prev, next as TitleSnapshot)
    : diffCollection(prev, next as CollectionSnapshot, now);
}

function diffTitle(prev: TitleSnapshot, next: TitleSnapshot): DiffEvent[] {
  const events: DiffEvent[] = [];
  const slots = new Set<DateSlot>([
    ...(Object.keys(prev.releases) as DateSlot[]),
    ...(Object.keys(next.releases) as DateSlot[]),
  ]);

  for (const slot of slots) {
    const before = prev.releases[slot] ?? null;
    const after = next.releases[slot] ?? null;

    if (before === null && after === null) continue;

    if (before === null && after !== null) {
      // The primary slot gaining a date is "it has a release date now".
      // Any other slot gaining one is "the audiobook is happening" — a
      // different sentence, and often the one people actually waited for.
      events.push(
        slot === 'primary'
          ? { type: 'date_announced', payload: { slot, to: after } }
          : { type: 'format_added', payload: { slot, on: after } },
      );
      continue;
    }

    if (before !== null && after === null) {
      // Only the primary date disappearing is news — that is a book being
      // pulled from the schedule. A format quietly vanishing from a
      // catalogue is almost always an editing accident upstream, and
      // reporting it would be crying wolf.
      if (slot === 'primary') {
        events.push({ type: 'date_removed', payload: { slot, from: before } });
      }
      continue;
    }

    if (before !== null && after !== null && !sameDate(before, after)) {
      events.push({
        type: isRefinement(before, after) ? 'date_refined' : 'date_changed',
        payload: { slot, from: before, to: after },
      });
    }
  }

  return events;
}

function diffCollection(
  prev: CollectionSnapshot,
  next: CollectionSnapshot,
  now: Date,
): DiffEvent[] {
  const events: DiffEvent[] = [];

  // "The series is finished" is worth knowing: it is the signal to stop
  // waiting, and the only thing that legitimately retires a placeholder.
  if (prev.completion !== 'completed' && next.completion === 'completed') {
    events.push({ type: 'series_completed', payload: { from: prev.completion } });
  }

  const known = new Set(prev.works.map((w) => w.sourceId));

  // Judge each work on its own date rather than suppressing the whole batch
  // on size. A catalogue importing twenty of an author's old books and an
  // author genuinely announcing six new ones look identical by count and
  // nothing alike by content.
  const fresh = next.works
    .filter((w) => !known.has(w.sourceId))
    .filter((w) => !isBackfill(w, now))
    .slice(0, SANITY_CAP);

  for (const work of fresh) {
    events.push({
      type: 'work_added',
      payload: {
        work: {
          sourceId: work.sourceId,
          title: work.title,
          position: work.position,
          releaseDate: work.releaseDate,
        },
      },
    });
  }

  return events;
}

/**
 * How hard an event should push.
 *
 * This is *only* about the changes feed. The date itself always updates
 * everywhere in the app regardless — when "2027" becomes "12 Mar 2027" that
 * is the date from then on. The question here is narrower: does this deserve
 * to interrupt you, or should it just be there when you look?
 *
 * A schedule firming up is real information and belongs in the history, but
 * treating it like a delay would cry wolf every time a publisher pins a month
 * down. Note that quiet is not the same as bad news: a date being pulled is
 * unwelcome and very much demands attention.
 */
export type Prominence = 'notable' | 'quiet';

export function prominenceOf(type: DiffEvent['type']): Prominence {
  return type === 'date_refined' ? 'quiet' : 'notable';
}

/**
 * Is this fetch worth trusting?
 *
 * The failure mode this exists for: a source briefly returns an empty or
 * truncated works list. Works vanishing is ignored, so no events fire — but
 * the empty snapshot gets *written*, and on the next poll every work looks
 * brand new and the whole back catalogue fires at once. Guarding the write is
 * the fix; capping the events downstream would only have hidden it.
 *
 * Titles are deliberately not guarded: a title losing its date is exactly the
 * `date_removed` case this app exists to report.
 */
export function isSuspectSnapshot(prev: Snapshot | null, next: Snapshot): boolean {
  if (prev === null) return false;
  if (prev.kind !== 'collection' || next.kind !== 'collection') return false;
  if (prev.works.length < 3) return false;
  return next.works.length < prev.works.length * SUSPECT_LOSS_RATIO;
}

/**
 * A work that appears in the list for the first time but came out years ago
 * is the catalogue catching up, not an announcement. Undated works are always
 * treated as news — an undated entry appearing under an author you follow is
 * precisely the announcement this app exists to catch.
 */
function isBackfill(work: WorkRef, now: Date): boolean {
  if (work.releaseDate === null) return false;
  const cutoff = new Date(now.getTime() - BACKFILL_AGE_DAYS * 86_400_000);
  return hasPassed(work.releaseDate, cutoff);
}

/**
 * Dates passing is not a snapshot change, so it can never come out of a diff.
 * This is the other half: which slots have come out *since we last looked*?
 *
 * "Since we last looked" is the whole point, and getting it wrong is loud.
 * Adding the 1965 Dune and asking only "has this date passed?" announces its
 * hardback, ebook, paperback and audiobook as four pieces of breaking news
 * about a sixty-year-old novel. `since` is the previous check time: a slot
 * fires only if it had not already passed by then, so something that was
 * out before you ever added it stays silent forever.
 *
 * `since` of null means first sight, where nothing can have changed yet.
 * `alreadyAnnounced` guards against repeats if a poll is replayed.
 */
export function findReleases(
  snapshot: Snapshot,
  now: Date,
  since: Date | null,
  alreadyAnnounced: ReadonlySet<string> = new Set(),
): DiffEvent[] {
  if (snapshot.kind !== 'title') return [];
  if (since === null) return [];

  const events: DiffEvent[] = [];
  for (const slot of ['primary', ...NON_PRIMARY_SLOTS] as DateSlot[]) {
    if (alreadyAnnounced.has(slot)) continue;
    const date = snapshot.releases[slot] ?? null;
    if (date === null) continue;
    if (!hasPassed(date, now)) continue;
    // Already out last time we checked, so it is not news now.
    if (hasPassed(date, since)) continue;
    events.push({ type: 'released', payload: { slot, on: date } });
  }
  return events;
}

/** Convenience for adapters: normalise a raw source string into a slot value. */
export function toRelease(raw: string | null | undefined): SlateDate | null {
  return parseDate(raw);
}
